import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { FloatingEditorBar, PortionSizeSelector } from "@chef/components";
import {
  language,
  PRODUCT_TYPE_IDS,
  BRAND_PATHS,
  PRODUCT_IDS,
  FEATURE_FLAGS,
} from "@chef/constants";
import {
  openModal,
  selectDeviationProducts,
  showNotification,
  useBillingQuery,
  usePickAndMixQuery,
  useProductsByIdsQuery,
  useRecommendedRecipesQuery,
} from "@chef/state-management";
import {
  useAppDispatch,
  useAppSelector,
  useCalendarData,
  useExtendedBasketData,
  useFavorites,
  useFilters,
  useTrack,
  useUpdateBasketPickAndMixDeviation,
} from "@chef/state-management/hooks";
import {
  formatDate,
  getCutoffDateForWeek,
  isMealboxProduct,
  isPickAndMixProduct,
} from "@chef/helpers";
import { FILTER_CATEGORIES } from "@chef/constants";
import {
  filterPickAndMix,
  generateFilterCategoriesFromRecipes,
  getAttribute,
  getBasketDataFromCalendar,
  getBasketFinances,
  getIsBeforeStartDateFromCalendar,
} from "@chef/state-management/helpers";
import { useEffectOnce, useIntercom } from "@chef/hooks";

import {
  BackToTopButton,
  ErrorComponent,
  PageLoader,
  RatingConsentDisclaimer,
  FilterNavigation,
  RATING_CONSENT_TYPE,
} from "@chef/smart-components";
import { AppShell } from "@chef/feature-app-shell";
import { useFlag } from "@chef/feature-flags";
import { useLoyalty } from "@chef/state-management/hooks";
import { isEqualStrings } from "@chef/utils/equal";
import { unique } from "@chef/utils/array";

import { getSteps, getTotalProductCount } from "../utils";
import {
  useCreateDeviationBasket,
  usePickAndMixProductWithPrice,
  useSortedPickAndMix,
} from "../hooks";
import { PickAndMixCardGrid } from "../lib/PickAndMixCardGrid";

import { intl } from "./WeekEditorPickAndMix.Intl";

interface Props {
  week: number;
  year: number;
}

export const WeekEditorPickAndMix = ({ week, year }: Props) => {
  const router = useRouter();
  const { enabled: isWeekEditorRedesignEnabled } = useFlag(
    FEATURE_FLAGS.WEEK_EDITOR_REDESIGN,
  );

  const {
    enabled: isPrioritiseRecommendationsEnabled,
    isReady: isPrioritiseRecommendationsReady,
  } = useFlag(FEATURE_FLAGS.PRIORITISE_RECOMMENDATIONS_FILTER);

  const dispatch = useAppDispatch();
  const track = useTrack();
  const { updateUrl } = useFilters();

  useIntercom(false);

  const selectedProducts = useAppSelector(
    selectDeviationProducts({ week, year }),
  );

  const {
    data: billingData,
    isLoading: isLoadingBillingData,
    isError: isErrorBillingData,
    error: billingDataError,
  } = useBillingQuery();

  const {
    data: extendedBasketData,
    isLoading: isLoadingExtendedBasketData,
    isError: isErrorExtendedBasketData,
  } = useExtendedBasketData({ week, year });

  const portions = extendedBasketData.portions;

  const minMeals = extendedBasketData.minMeals;

  // queries
  const {
    updateBasketPickAndMixDeviation,
    isLoading: isLoadingUpdateBasketPickAndMixDeviation,
  } = useUpdateBasketPickAndMixDeviation({ week, year });

  const { isLoading: isLoadingCreateDeviationBasket } =
    useCreateDeviationBasket({ week, year });

  const { data: loyaltyData } = useLoyalty();

  const [currentFilter, setCurrentFilter] = useState<string>(
    FILTER_CATEGORIES.ALL_CATEGORIES_SLUG,
  );

  const { favorites, isReady: favoritesIsReady } = useFavorites();

  const { data: _recommendedRecipes } = useRecommendedRecipesQuery({
    week,
    year,
  });

  const {
    data: { pickAndMix } = {},
    isError: isErrorPickAndMix,
    isLoading: isLoadingPickAndMix,
    error: pickAndMixError,
  } = usePickAndMixQuery({
    productTypeId: PRODUCT_TYPE_IDS.PICKANDMIX,
    week,
    year,
  });

  const {
    calendar,
    isError: isErrorCalendar,
    isLoading: isLoadingCalendar,
    calendarError,
  } = useCalendarData({
    week,
    year,
  });

  const {
    data: { products = [] } = {},
    isError: isErrorProductsByIds,
    isLoading: isLoadingProductsByIds,
    error: productsByIdsError,
  } = useProductsByIdsQuery({
    productIds: [PRODUCT_IDS.ONESUB],
    week,
    year,
  });

  const filteredPickAndMix = useMemo(() => {
    return filterPickAndMix({
      pickAndMix: pickAndMix || [],
      cutoffDate: getCutoffDateForWeek({ week, year }),
      portions,
    });
  }, [pickAndMix, portions, week, year]);

  const filteredPortionOptions = useMemo(() => {
    const selectedProduct = selectedProducts?.find(isMealboxProduct);

    if (!selectedProduct) {
      return [];
    }

    if (!products) {
      return [];
    }

    const portions = unique(
      products
        .find((p) => isEqualStrings(p.productId, selectedProduct.productId))
        ?.variations.map((v) => +(getAttribute("Portions", v) || 0)) || [],
    ).sort((a, b) => a - b);

    if (portions === null) {
      return [];
    }

    return portions
      .map((p) => ({
        value: p,
        name: p.toString(),
      }))
      .sort((a, b) => a.value - b.value);
  }, [products, selectedProducts]);

  const { pickAndMix: pnmxWithPrices } = usePickAndMixProductWithPrice({
    week,
    year,
    sortedPickAndMix: filteredPickAndMix,
    canSelectProducts: true,
  });

  const { sortedPickAndMix } = useSortedPickAndMix({
    pickAndMix: pnmxWithPrices,
    week,
    year,
  });

  const recommendedProductIdsForWeek = useMemo(() => {
    const productIdsWithScore = new Map<string, number>();
    const recommendedRecipes =
      _recommendedRecipes?.recommendedRecipes?.recipes ?? [];

    const sortedRecommendedRecipes = [...recommendedRecipes].sort(
      (a, b) => b.score - a.score,
    );

    for (const recipe of sortedRecommendedRecipes) {
      const productId = pnmxWithPrices
        .flat()
        .find((pnm) =>
          pnm.recipes.some((r) => r.mainRecipeId === recipe.mainRecipeId),
        )?.productId;

      if (productId) {
        productIdsWithScore.set(productId, recipe.score);
      }
    }

    return productIdsWithScore;
  }, [pnmxWithPrices, _recommendedRecipes]);

  const filters = useMemo(() => {
    if (!favoritesIsReady || !isPrioritiseRecommendationsReady) {
      return [];
    }

    const recipes = pnmxWithPrices.flatMap((pnm) =>
      pnm.recipes.map((r) => ({ ...r, productId: pnm.productId })),
    );

    return generateFilterCategoriesFromRecipes({
      recipes,
      currentFilter,
      recommendations: recommendedProductIdsForWeek,
      favorites,
      prioritiseRecommendations: !!isPrioritiseRecommendationsEnabled,
    });
  }, [
    pnmxWithPrices,
    recommendedProductIdsForWeek,
    currentFilter,
    favorites,
    favoritesIsReady,
    isPrioritiseRecommendationsReady,
    isPrioritiseRecommendationsEnabled,
  ]);

  const onCartClick = () => {
    dispatch(openModal({ id: "DEVIATION_CART", week, year }));
  };

  const steps = getSteps();

  const isError =
    isErrorCalendar ||
    isErrorPickAndMix ||
    isErrorProductsByIds ||
    isErrorExtendedBasketData ||
    isErrorBillingData;

  const isLoading =
    router.isFallback ||
    isLoadingPickAndMix ||
    isLoadingCalendar ||
    isLoadingProductsByIds ||
    isLoadingCreateDeviationBasket ||
    isLoadingUpdateBasketPickAndMixDeviation ||
    isLoadingExtendedBasketData ||
    isLoadingBillingData ||
    !favoritesIsReady;

  // Tracking
  useEffectOnce(() => {
    track("deviationUpdateStepViewed", {
      affiliation: "Frontend process",
      delivery_update_step: 1,
      delivery_update_step_title: intl.CHOOSE_DISHES,
      delivery_update_step_name: "userPickAndMix",
    });

    if (!selectedProducts) {
      return;
    }

    track("deviationUpdateStarted", {
      affiliation: "Frontend process",
      products: selectedProducts.map((bp) => ({
        product_id: bp.productId,
        variant_id: bp.variationId,
        quantity: bp.quantity,
        price: bp.price,
        category_id: bp.productTypeId,
      })),
      week,
      year,
      status: billingData?.billing.status,
      total: price,
    });
  }, !isLoading && !isError);

  useEffect(() => {
    const filter = router.query["filter"];
    if (filter) {
      if (typeof filter === "string") {
        setCurrentFilter(filter);
      } else {
        console.error("filter is not a string");
      }
    }
  }, [router.query]);

  // if the user can't get deliveries for the given week/year
  const { mealboxBasket } =
    getBasketDataFromCalendar({
      calendar: calendar || [],
      week,
      year,
    }) || {};
  const shippingDetails = mealboxBasket?.shippingDetails;

  const plannedDeliveryInformation =
    shippingDetails?.plannedDeliveryInformation;

  const isBeforeStartWeek = getIsBeforeStartDateFromCalendar({
    calendar: calendar || [],
    week,
    year,
  });

  useEffectOnce(() => {
    router.push(BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year }));
  }, !!calendar && !plannedDeliveryInformation?.actualTimeblockId);

  useEffectOnce(() => {
    showNotification({
      type: "error",
      message: intl.NO_DELIVERIES_BEFORE_START_WEEK,
    });
    router.push(BRAND_PATHS.MY_MENU_PAGE);
  }, !!calendar && !!isBeforeStartWeek);

  const handlePortionsChange = (portions: number) => {
    updateBasketPickAndMixDeviation({
      basketProducts: selectedProducts || [],
      portions,
    });
  };

  const handleButtonClick = () => {
    track("deviationUpdateStepCompleted", {
      affiliation: "Frontend process",
      delivery_update_step: 1,
      delivery_update_step_title: intl.CHOOSE_DISHES,
      delivery_update_step_name: "userPickAndMix",
    });

    router.push(BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year }));
  };

  const filterInformation = filters.find((f) =>
    isEqualStrings(f.value, currentFilter),
  );

  if (isError) {
    return (
      <ErrorComponent
        context={{
          calendarError,
          pickAndMixError,
          productsByIdsError,
          billingDataError,
        }}
      />
    );
  }

  if (isLoading) {
    return <PageLoader />;
  }

  const deliveryDateString =
    plannedDeliveryInformation?.deliveryDate &&
    formatDate(plannedDeliveryInformation.deliveryDate, undefined, {
      lowerCase: true,
    });

  const totalBasketItemCount = getTotalProductCount(selectedProducts || []);
  const price = getBasketFinances({
    basketProducts: selectedProducts || [],
    customerFee: plannedDeliveryInformation?.customerFee,
    loyaltyLevel: loyaltyData?.currentLevel,
  }).total;

  return (
    <AppShell title={intl.CHOOSE_DISHES}>
      <AppShell.Meta />

      <AppShell.Drawer />

      {isWeekEditorRedesignEnabled ? (
        <AppShell.Sticky anchor="top" float>
          <AppShell.Header variant="my-menu" />
        </AppShell.Sticky>
      ) : (
        <AppShell.Sticky anchor="top">
          <AppShell.Header
            variant="steps"
            steps={steps}
            current={1}
            closeHref={BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year })}
            exitLabel={intl.HEADER_BACK}
            cartCount={totalBasketItemCount}
            onCartClick={onCartClick}
          />
        </AppShell.Sticky>
      )}

      <AppShell.Main>
        <h1 className="hidden mt-6 mb-2 md:block">
          <strong>{intl.CHOOSE_DISHES}</strong>
        </h1>
        <div className="mb-2 text-center md:text-left md:mt-0">
          {intl.DELIVERED} {deliveryDateString}
        </div>

        <RatingConsentDisclaimer type={RATING_CONSENT_TYPE.WEEK_EDITOR} />

        <div className="mt-4 md:hidden">
          <PortionSizeSelector
            name="top"
            language={language}
            options={filteredPortionOptions}
            value={portions}
            onChange={handlePortionsChange}
          />
        </div>

        <FilterNavigation
          items={filters}
          value={currentFilter}
          onChange={(val) => {
            updateUrl(val);
          }}
          resetFilters={() => updateUrl(FILTER_CATEGORIES.ALL_CATEGORIES_SLUG)}
          className="mt-4"
          leftClassName="bg-gradient-to-r from-background via-background"
          rightClassName="bg-gradient-to-l from-background via-background"
          showGradients
        />

        <PickAndMixCardGrid
          items={sortedPickAndMix}
          filter={filterInformation}
          recommendations={recommendedProductIdsForWeek}
          portions={portions}
          resetFilters={() => updateUrl(FILTER_CATEGORIES.ALL_CATEGORIES_SLUG)}
          onModalAction={onCartClick}
          week={week}
          year={year}
          className="mt-2"
        />

        <BackToTopButton />
      </AppShell.Main>

      {isWeekEditorRedesignEnabled ? (
        <AppShell.Sticky anchor="bottom">
          <AppShell.TabBar variant="my-menu" />
        </AppShell.Sticky>
      ) : (
        <FloatingEditorBar
          price={price}
          discount={0}
          language={language}
          basketCount={
            selectedProducts?.filter(isPickAndMixProduct).length || 0
          }
          minimumBasketCount={minMeals}
          selectedPortion={portions}
          portions={filteredPortionOptions}
          onPortionChange={handlePortionsChange}
          onButtonClick={handleButtonClick}
          onShowBasket={onCartClick}
        />
      )}
    </AppShell>
  );
};
